'use client';
import { FC, useState } from 'react';
import { useSession } from 'next-auth/react';
import { useT } from '@/libs/i18n/utils/useT';
import { WidgetContainer } from '@/components/modules/Widget/Container';
import { copyTextToClipboard } from '@/utils/clipboard';
import { IfRender } from '@/utils/jsx';
import { useSendConfirmationPresence } from '../../http/client/mutations/useSendConfirmationPresence';
import { useGetNextEvents } from '../../http/client/useGetListNextsEvents';
import { CardEvent } from '../CardEvent/CardEvent';
import { ModalConfirmedEvent } from '../ModalConfirmedEvent/ModalConfirmedEvent';
import { ModalShareEvent } from '../ModalShareEvent/ModalShareEvent';
import { NoDataEventsWidget } from '../NoDataEventsWidget/NoDataEventsWidget';
import { SkeletonWidgetListSlide } from '../SkeletonWidgetListSlide/SkeletonWidgetListSlide';

export const NextEvents: FC = () => {
  const t = useT();
  const { data: session } = useSession();

  const { data: events = [], isFetching } = useGetNextEvents();

  const [idEventToShowModalPresence, setIdEventToShowModalPresence] = useState<
    string | null
  >(null);

  const [linkUrlToOpenModalShare, setLinkUrlToOpenModalShare] = useState<
    string | null
  >(null);

  const shareEvent = async (linkFullToShare: string) =>
    copyTextToClipboard(linkFullToShare || '');

  const handleClickShareEvent = async (link: string) => {
    if (!session || !session.access_token) {
      window.dispatchEvent(new Event('open-unauthenticated-modal'));
      return;
    }

    setLinkUrlToOpenModalShare(link);
  };

  const { mutate: sendConfirmation, status } = useSendConfirmationPresence();

  const requestsubscription = (eventId: string) => {
    setIdEventToShowModalPresence(eventId);
    sendConfirmation(eventId);
  };

  if (isFetching && !idEventToShowModalPresence) {
    return (
      <SkeletonWidgetListSlide title={t(`screens.events.titleWidgets.next`)} />
    );
  }

  return (
    <>
      <ModalConfirmedEvent
        t={t}
        isOpen={!!idEventToShowModalPresence}
        onClose={() => setIdEventToShowModalPresence(null)}
      />

      <ModalShareEvent
        t={t}
        isOpen={!!linkUrlToOpenModalShare}
        onClose={() => setLinkUrlToOpenModalShare(null)}
        copyLinkShare={shareEvent}
        linkUrl={linkUrlToOpenModalShare}
      />

      <IfRender condition={!events.length && !isFetching}>
        <NoDataEventsWidget />
      </IfRender>
      <IfRender condition={!!events.length && !isFetching}>
        <WidgetContainer
          containerId={`event-list-widget-next`}
          title={t(`screens.events.titleWidgets.next`)}
          removeSeeAllButton
        >
          {events.map((event, idx) => (
            <CardEvent
              key={idx}
              isLoading={
                status === 'pending' && idEventToShowModalPresence === event.id
              }
              event={event}
              requestsubscription={requestsubscription}
              shareEvent={(link: string) => handleClickShareEvent(link)}
            />
          ))}
        </WidgetContainer>
      </IfRender>
    </>
  );
};
